import axios from 'axios';
import type {
    Client,
    Product,
    ProductsResponse,
    StatsResponse,
    SearchResponse,
    QuizResponse,
    QuizAnswers,
    AnalyticsSummary,
    SearchAnalytics,
    ViewAnalytics
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// ============================================
// CLIENT APIs
// ============================================

export const getClients = async (): Promise<Client[]> => {
    const { data } = await api.get('/api/clients');
    return data;
};

export const getClient = async (clientId: string): Promise<Client> => {
    const { data } = await api.get(`/api/clients/${clientId}`);
    return data;
};

export const createClient = async (client: Partial<Client>): Promise<Client> => {
    const { data } = await api.post('/api/clients', client);
    return data;
};

export const updateClient = async (clientId: string, client: Partial<Client>): Promise<Client> => {
    const { data } = await api.put(`/api/clients/${clientId}`, client);
    return data;
};

export const deleteClient = async (clientId: string): Promise<void> => {
    await api.delete(`/api/clients/${clientId}`);
};

// ============================================
// PRODUCT APIs
// ============================================

export const getProducts = async (
    clientId: string,
    page = 1,
    limit = 20,
    search = ''
): Promise<ProductsResponse> => {
    const { data } = await api.get(`/api/clients/${clientId}/products`, {
        params: { page, limit, search }
    });
    return data;
};

export const getProduct = async (id: number): Promise<Product> => {
    const { data } = await api.get(`/api/products/${id}`);
    return data;
};

export const createProduct = async (clientId: string, product: Partial<Product>): Promise<Product> => {
    const { data } = await api.post(`/api/clients/${clientId}/products`, product);
    return data;
};

export const updateProduct = async (id: number, product: Partial<Product>): Promise<Product> => {
    const { data } = await api.put(`/api/products/${id}`, product);
    return data;
};

export const deleteProduct = async (id: number): Promise<void> => {
    await api.delete(`/api/products/${id}`);
};

export const enrichProduct = async (id: number): Promise<{ success: boolean; product: Product }> => {
    const { data } = await api.post(`/api/products/${id}/enrich`);
    return data;
};

export const getStats = async (clientId: string): Promise<StatsResponse> => {
    const { data } = await api.get(`/api/clients/${clientId}/stats`);
    return data;
};

export const syncVectors = async (clientId: string): Promise<{ success: boolean; message: string; synced: number }> => {
    const { data } = await api.post(`/api/clients/${clientId}/sync-vectors`);
    return data;
};

export const importProducts = async (clientId: string, file: File): Promise<{ imported: number; errors: string[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post(`/api/clients/${clientId}/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
};

export const downloadTemplate = (): void => {
    // Direct download - opens in new tab/triggers download
    window.open(`${API_URL}/api/template`, '_blank');
};

// ============================================
// KIOSK APIs (Customer Facing)
// ============================================

export const getCatalog = async (
    clientId: string,
    page = 1,
    limit = 20,
    search = ''
): Promise<ProductsResponse> => {
    const { data } = await api.get(`/api/kiosk/${clientId}/catalog`, {
        params: { page, limit, search }
    });
    return data;
};

export const searchProducts = async (
    clientId: string,
    query: string,
    limit = 10
): Promise<SearchResponse> => {
    const { data } = await api.post(`/api/kiosk/${clientId}/search`, { query, limit });
    return data;
};

export const getQuizRecommendations = async (
    clientId: string,
    answers: QuizAnswers
): Promise<QuizResponse> => {
    const { data } = await api.post(`/api/kiosk/${clientId}/quiz`, { answers });
    return data;
};

export const getKioskProduct = async (clientId: string, id: number): Promise<Product> => {
    const { data } = await api.get(`/api/kiosk/${clientId}/products/${id}`);
    return data;
};

// ============================================
// ANALYTICS APIs
// ============================================

export const getAnalyticsSummary = async (clientId: string, days = 7): Promise<AnalyticsSummary> => {
    const { data } = await api.get(`/api/clients/${clientId}/analytics/summary`, {
        params: { days }
    });
    return data;
};

export const getSearchAnalytics = async (clientId: string, days = 30): Promise<SearchAnalytics> => {
    const { data } = await api.get(`/api/clients/${clientId}/analytics/searches`, {
        params: { days }
    });
    return data;
};

export const getViewAnalytics = async (clientId: string, days = 30): Promise<ViewAnalytics> => {
    const { data } = await api.get(`/api/clients/${clientId}/analytics/views`, {
        params: { days }
    });
    return data;
};

export default api;
